import type { FC, ReactNode } from 'react'

import { ArrowUpRight, Plus } from 'lucide-react'
import { Eyebrow } from '@/components/landing/section-intro'
import { appLinks } from '@/constants/site'

const linkClass = 'font-medium text-brand underline-offset-4 hover:underline'

const ExternalLink: FC<{ href: string, children: ReactNode }> = ({ href, children }): ReactNode => {
    return (
        <a href={href} target='_blank' rel='noopener noreferrer' className={linkClass}>
            {children}
            <span className='sr-only'> (opens in a new tab)</span>
        </a>
    )
}

const FAQS: { question: string, answer: ReactNode }[] = [
    {
        question: 'What can I do for free?',
        answer: (
            <p>
                Play local videos and photos in the classic 1, 2 and 4-view grids, with no ads during local playback. You can also export compatible local scenes of up to 30 seconds at 720p. None of this requires watching an ad.
            </p>
        )
    },
    {
        question: 'What do I get for watching an ad?',
        answer: (
            <p>
                In supported versions, finishing an optional rewarded ad unlocks one 1080p export of up to 30 seconds from a free composition. It does not unlock PRO or Ghost Mode. The reward is only used once the MP4 saves successfully, so if an export fails or is canceled it stays available for another try on the same installation. Ads may not always be available.
            </p>
        )
    },
    {
        question: 'Can I play 12 videos at once?',
        answer: (
            <p>
                Some layouts offer up to 12 views. Whether that many sources play together smoothly depends on their format, resolution and your device's capabilities, so 12 views do not guarantee 12 simultaneous videos on every device.
            </p>
        )
    },
    {
        question: 'Are my videos uploaded to the cloud?',
        answer: (
            <p>
                No. Local files are processed on your device, and content only goes to a TV or a share destination when you choose to send it there. Your account, purchases, optional ads and optional diagnostics rely on outside services, explained in the
                {' '}
                <ExternalLink href={appLinks.privacy}>privacy policy</ExternalLink>
                .
            </p>
        )
    },
    {
        question: 'What can I export to MP4?',
        answer: (
            <p>
                Compatible local videos and photos. Free exports go up to 30 seconds at 720p, and PRO goes up to 1080p. Playlists do not advance during an export, animated frames are exported as still frames, and YouTube and network sources are excluded.
            </p>
        )
    },
    {
        question: 'How does it work with Google TV?',
        answer: (
            <p>
                You need the compatible apps on your phone and your TV, access to the feature on your plan, and both devices on the same local network. Then scan the QR code shown on the TV. The TV displays up to four views, photos open in their own viewer, and YouTube plays in its official full-screen player.
            </p>
        )
    },
    {
        question: 'Can I use YouTube?',
        answer: (
            <p>
                Yes, through the official YouTube player with its own controls. It needs an internet connection and a large enough view, so you may need to enlarge it. YouTube content cannot be downloaded or exported to MP4 from the app.
            </p>
        )
    },
    {
        question: 'Where do I manage PRO or delete my account?',
        answer: (
            <p>
                Subscriptions are managed in
                {' '}
                <ExternalLink href={appLinks.subscriptions}>Google Play</ExternalLink>
                . To delete your account, use the option in your in-app profile or the
                {' '}
                <ExternalLink href={appLinks.deleteAccount}>account and data deletion page</ExternalLink>
                . Deleting your account does not cancel a subscription or remove your local videos.
            </p>
        )
    }
]

const FaqSection: FC = (): ReactNode => {
    return (
        <section id='faq' aria-labelledby='faq-title' className='border-t border-border bg-background py-20 lg:py-28'>
            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:gap-20'>
                <div className='reveal lg:sticky lg:top-28 lg:self-start'>
                    <Eyebrow>
                        Frequently asked questions
                    </Eyebrow>

                    <h2
                        id='faq-title'
                        className='mt-4 font-heading text-[clamp(2.25rem,5vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-foreground'
                    >
                        Quick
                        <br />
                        answers.
                    </h2>

                    <p className='mt-7 text-base text-muted-foreground'>
                        Need help?
                    </p>

                    <a
                        href={`mailto:${appLinks.supportEmail}`}
                        className='group mt-3 inline-flex items-center gap-2 text-sm font-bold text-brand'
                    >
                        Contact support
                        <ArrowUpRight className='size-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </a>
                </div>

                <div className='border-t border-border'>
                    {FAQS.map((faq) => (
                        <details key={faq.question} className='disclosure group border-b border-border'>
                            <summary className='flex cursor-pointer items-center justify-between gap-6 py-5 text-left text-base font-medium text-foreground transition-colors hover:text-brand sm:text-[1.05rem]'>
                                {faq.question}

                                <Plus className='size-5 shrink-0 text-brand transition-transform duration-300 group-open:rotate-45' strokeWidth={1.8} />
                            </summary>

                            <div className='max-w-2xl pb-6 pr-8 text-[0.95rem] leading-relaxed text-muted-foreground'>
                                {faq.answer}
                            </div>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default FaqSection
